/**
 * The effects layer.
 *
 * A single canvas above the table. Particles are the one thing worth taking out
 * of the DOM: a climax throws up to ninety of them, and many elements
 * animating at once will drop frames on a phone where batched canvas draws
 * will not.
 *
 * Everything here is decoration. If it never ran, the game would still be
 * playable and correct — which is why reduced motion simply turns it off.
 */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  hue: number;
  /** Squares tumble, circles do not. */
  spin: number;
  rotation: number;
  shape: "spark" | "confetti" | "coin";
  gravity: number;
}

const TAU = Math.PI * 2;

export class Effects {
  #canvas: HTMLCanvasElement | null = null;
  #ctx: CanvasRenderingContext2D | null = null;
  #particles: Particle[] = [];
  #frame: number | null = null;
  #last = 0;
  #dpr = 1;
  #enabled = true;
  /** Screen shake, in pixels, decaying to zero. */
  #shake = 0;
  #onShake: ((offsetX: number, offsetY: number) => void) | null = null;

  attach(canvas: HTMLCanvasElement | null, onShake?: (x: number, y: number) => void): void {
    this.#canvas = canvas;
    this.#ctx = canvas?.getContext("2d") ?? null;
    this.#onShake = onShake ?? null;
    if (canvas !== null) this.resize();
  }

  setEnabled(enabled: boolean): void {
    this.#enabled = enabled;
    if (!enabled) this.clear();
  }

  resize(): void {
    const canvas = this.#canvas;
    if (canvas === null) return;
    this.#dpr = Math.min(2, window.devicePixelRatio || 1);
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = Math.round(width * this.#dpr);
    canvas.height = Math.round(height * this.#dpr);
  }

  clear(): void {
    this.#particles = [];
    this.#shake = 0;
    const ctx = this.#ctx;
    const canvas = this.#canvas;
    if (ctx !== null && canvas !== null) ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.#onShake?.(0, 0);
  }

  detach(): void {
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    // Drop the particles too. Keeping them meant a canvas reattached later
    // resumed a burst from a table nobody is looking at any more.
    this.#particles = [];
    this.#shake = 0;
    this.#onShake?.(0, 0);
    this.#canvas = null;
    this.#ctx = null;
  }

  /** A bomb going off. `power` scales the blast from a four-card to four kings. */
  bomb(x: number, y: number, power: number, shake = 0, durationMs = 360): void {
    if (!this.#enabled) return;
    const big = power >= 1000;
    const count = big ? 90 : power >= 60 ? 40 : 24;
    const speed = big ? 640 : power >= 60 ? 480 : 380;

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * TAU;
      const velocity = speed * (0.35 + Math.random() * 0.65);
      this.#particles.push({
        x, y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        life: 0,
        maxLife: durationMs / 1000 * (0.65 + Math.random() * 0.35),
        size: 2 + Math.random() * (big ? 6 : 4),
        // Sunset palette: gold through orange to red.
        hue: big ? 280 + Math.random() * 60 : 20 + Math.random() * 35,
        spin: 0,
        rotation: 0,
        shape: "spark",
        gravity: 520,
      });
    }
    this.#shake = shake;
    this.#start();
  }

  /** Confetti from the top, for a hand or match won. */
  confetti(width: number): void {
    if (!this.#enabled) return;
    for (let i = 0; i < 140; i++) {
      this.#particles.push({
        x: Math.random() * width,
        y: -20 - Math.random() * 120,
        vx: (Math.random() - 0.5) * 90,
        vy: 90 + Math.random() * 160,
        life: 0,
        maxLife: 0.9 + Math.random() * 0.5,
        size: 5 + Math.random() * 6,
        hue: Math.random() * 360,
        spin: (Math.random() - 0.5) * 9,
        rotation: Math.random() * TAU,
        shape: "confetti",
        gravity: 40,
      });
    }
    this.#start();
  }

  /** Coins arcing from a point towards the status bar. */
  coins(x: number, y: number, count = 14): void {
    if (!this.#enabled) return;
    for (let i = 0; i < count; i++) {
      this.#particles.push({
        x, y,
        vx: (Math.random() - 0.5) * 260,
        vy: -220 - Math.random() * 200,
        life: 0,
        maxLife: 0.85 + Math.random() * 0.5,
        size: 5 + Math.random() * 3,
        hue: 45,
        spin: (Math.random() - 0.5) * 12,
        rotation: 0,
        shape: "coin",
        gravity: 620,
      });
    }
    this.#start();
  }

  /** A soft puff where a card lands. */
  puff(x: number, y: number): void {
    if (!this.#enabled) return;
    for (let i = 0; i < 12; i++) {
      const angle = Math.random() * TAU;
      this.#particles.push({
        x, y,
        vx: Math.cos(angle) * 70 * Math.random(),
        vy: Math.sin(angle) * 70 * Math.random() - 30,
        life: 0,
        maxLife: 0.3 + Math.random() * 0.2,
        size: 2 + Math.random() * 2,
        hue: 40,
        spin: 0,
        rotation: 0,
        shape: "spark",
        gravity: 120,
      });
    }
    this.#start();
  }

  #start(): void {
    if (this.#frame !== null) return;
    this.#last = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #tick = (now: number): void => {
    const ctx = this.#ctx;
    const canvas = this.#canvas;
    this.#frame = null;
    if (ctx === null || canvas === null) return;

    // Clamped, so a backgrounded tab returning does not teleport every particle
    // off screen in one enormous step.
    const dt = Math.min(0.05, (now - this.#last) / 1000);
    this.#last = now;

    ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const alive: Particle[] = [];
    for (const p of this.#particles) {
      p.life += dt;
      if (p.life >= p.maxLife) continue;
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rotation += p.spin * dt;

      const fade = 1 - p.life / p.maxLife;
      ctx.globalAlpha = Math.max(0, fade);

      if (p.shape === "confetti") {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = `hsl(${p.hue} 85% 62%)`;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      } else if (p.shape === "coin") {
        ctx.save();
        ctx.translate(p.x, p.y);
        // Squashing horizontally reads as a coin tumbling edge-on.
        ctx.scale(Math.abs(Math.cos(p.rotation)) * 0.8 + 0.2, 1);
        ctx.beginPath();
        ctx.arc(0, 0, p.size, 0, TAU);
        ctx.fillStyle = "#ffc94a";
        ctx.fill();
        ctx.restore();
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * fade, 0, TAU);
        ctx.fillStyle = `hsl(${p.hue} 95% ${55 + fade * 25}%)`;
        ctx.fill();
      }
      alive.push(p);
    }
    ctx.globalAlpha = 1;
    this.#particles = alive;

    if (this.#shake > 0.4) {
      this.#shake *= Math.pow(0.86, dt * 60);
      this.#onShake?.((Math.random() * 2 - 1) * this.#shake, (Math.random() * 2 - 1) * this.#shake);
    } else if (this.#shake !== 0) {
      this.#shake = 0;
      this.#onShake?.(0, 0);
    }

    if (this.#particles.length > 0 || this.#shake > 0) {
      this.#frame = requestAnimationFrame(this.#tick);
    }
  };
}
